import { CommonModule, NgIf } from '@angular/common';
import { Component, Inject, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as forge from 'node-forge'; //https://www.npmjs.com/package/node-forge?activeTab=readme
import { Agree21Service } from '../../data/agree21.service';
import { DefaultDialogBoxComponent } from '@shared/components/dialog-boxes/default-dialog-box/default-dialog-box.component';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DialogService } from '@core/services/dialog.service';

function pinValidator(control: AbstractControl): { [key: string]: boolean } | null {
  const value: string = control.value || '';

  // 1. Pflichtfeld prüfen
  if (!value) {
    return { required: true };
  }

  // 2. Länge überprüfen
  if (value.length < 8 || value.length > 20) {
    return { pinRequirements: true };
  }

  // 3. Nur erlaubte Zeichen
  const allowedCharsRegex = /^[a-zA-Z0-9%&\/=\?\*\+;:,\.\-_]+$/;
  if (!allowedCharsRegex.test(value)) {
    return { pinRequirements: true };
  }

  // 4. Erstes Zeichen muss ein Buchstabe sein
  if (!/^[a-zA-Z]/.test(value)) {
    return { pinRequirements: true };
  }

  // 5. Mindestens ein Großbuchstabe
  if (!/[A-Z]/.test(value)) {
    return { pinRequirements: true };
  }

  // 6. Mindestens eine Ziffer
  if (!/\d/.test(value)) {
    return { pinRequirements: true };
  }

  // 7. Mindestens ein Sonderzeichen aus der erlaubten Liste
  const specialCharRegex = /[%&\/=\?\*\+;:,\.\-_]/;
  if (!specialCharRegex.test(value)) {
    return { pinRequirements: true };
  }

  return null;
}

@Component({
  selector: 'app-pin-entry',
  imports: [NgIf, TranslateModule, ReactiveFormsModule, CommonModule],
  templateUrl: './pin-entry.component.html',
  styleUrl: './pin-entry.component.scss',
})
export class PinEntryComponent implements OnInit {
  pinForm: FormGroup;
  encryptedPin: string | null = null;
  agree21Service = inject(Agree21Service);
  PUBLICKEY_PEM;

  constructor(
    public dialog: DialogService,
    private fb: FormBuilder,
    public dialogRef: DialogRef,
    public translate: TranslateService
  ) {
    this.pinForm = this.fb.group(
      {
        firstPin: ['', [Validators.required, pinValidator]],
        confirmPin: ['', [Validators.required, pinValidator]],
      },
      { validators: this.matchingPinsValidator.bind(this) } // wichtig: this binden
    );

    this.agree21Service.getPublicKey().subscribe({
      next: (key: string) => {
        const formattedKey = key.replace(/\\n/g, '\n');
        this.PUBLICKEY_PEM = formattedKey;
      },
    });
  }

  matchingPinsValidator(group: AbstractControl): { [key: string]: boolean } | null {
    const firstPin = group.get('firstPin')?.value;
    const confirmPin = group.get('confirmPin')?.value;

    return firstPin && confirmPin && firstPin !== confirmPin ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    const confirmInput = document.getElementById('confirmPin');
    confirmInput?.addEventListener('paste', (e) => e.preventDefault());
  }

  togglePasswordVisibility(input: HTMLInputElement): void {
    input.type = input.type === 'password' ? 'text' : 'password';
  }

  async encryptPIN(pin) {
    const publicKey = forge.pki.publicKeyFromPem(this.PUBLICKEY_PEM);
    const encrypted = publicKey.encrypt(pin, 'RSA-OAEP');
    const encoded = forge.util.encode64(encrypted);
    return encoded;
  }

  async onSubmit(): Promise<void> {
    if (this.pinForm.valid) {
      const firstPin = this.pinForm.get('firstPin')?.value;

      try {
        this.encryptedPin = await this.encryptPIN(firstPin);
        const onlineBankingObject = {
          encryptedPin: this.encryptedPin,
        };
        this.dialogRef.close({
          success: true,
          onlineBankingPublicKey: onlineBankingObject,
        });
      } catch (error) {
        this.showConfirmationDialog(() => {});
      }
    }
  }

  async showConfirmationDialog(callback) {
    const answer = await this.dialog
      .open(DefaultDialogBoxComponent, {
        data: {
          headerText: 'PIN must contain only numeric digits',
          confirm: 'okay',
        },
      })
      .afterClosed()
      .toPromise();

    if (answer === true) {
      callback();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }
}
